// frontend/src/components/admin/UserManagement.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { SUPER_ADMIN_EMAIL } from '../../config/appConfig';
import './UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 20,
    total: 0
  });

  // Edit user form
  const [editForm, setEditForm] = useState({
    email: '',
    dogecoin_wallet: '',
    is_active: true,
    is_confirmed: true
  });

  // Balance form
  const [balanceForm, setBalanceForm] = useState({
    balance_doge: 0,
    total_earned: 0,
    articles_read_count: 0
  });

  useEffect(() => {
    fetchUsers();
  }, [pagination.skip, pagination.limit]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await api.get(
        `/admin/users`,
        {
          params: {
            skip: pagination.skip,
            limit: pagination.limit
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setUsers(response.data.users);
      setPagination(prev => ({ ...prev, total: response.data.total }));
      setError('');
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await api.get(
        `/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (err) {
      console.error('Failed to fetch user detail:', err);
      throw err;
    }
  };

  const handleEditUser = async (user) => {
    const detail = await fetchUserDetail(user.user.id);
    setSelectedUser(detail);
    setEditForm({
      email: detail.user.email,
      dogecoin_wallet: detail.user.dogecoin_wallet || '',
      is_active: detail.user.is_active,
      is_confirmed: detail.user.is_confirmed
    });
    setBalanceForm({
      balance_doge: detail.balance?.balance_doge || 0,
      total_earned: detail.balance?.total_earned || 0,
      articles_read_count: detail.balance?.articles_read_count || 0
    });
    setShowEditModal(true);
  };

  const handleEditBalance = async (user) => {
    const detail = await fetchUserDetail(user.user.id);
    setSelectedUser(detail);
    setBalanceForm({
      balance_doge: detail.balance?.balance_doge || 0,
      total_earned: detail.balance?.total_earned || 0,
      articles_read_count: detail.balance?.articles_read_count || 0
    });
    setShowBalanceModal(true);
  };

  const saveUserEdit = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      // Save user fields
      await api.put(
        `/admin/users/${selectedUser.user.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Save balance fields together
      await api.put(
        `/admin/users/${selectedUser.user.id}/balance`,
        balanceForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowEditModal(false);
      fetchUsers();
      alert('User updated successfully');
    } catch (err) {
      console.error('Failed to update user:', err);
      alert('Failed to update user: ' + (err?.response?.data?.detail || err.message || err));
    }
  };

  const saveBalanceEdit = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await api.put(
        `/admin/users/${selectedUser.user.id}/balance`,
        balanceForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowBalanceModal(false);
      fetchUsers();
      alert('Balance updated successfully');
    } catch (err) {
      console.error('Failed to update balance:', err);
      alert('Failed to update balance');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Delete user ${user.user.email}? This will delete all their data.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      await api.delete(
        `/admin/users/${user.user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchUsers();
      alert('User deleted successfully');
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDoge = (amount) => {
    return amount ? `${parseFloat(amount).toFixed(2)} DOGE` : '0.00 DOGE';
  };

  if (loading && users.length === 0) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h2>👥 User & Rewards Management</h2>
        <div className="header-stats">
          <div className="stat-box">
            <span className="stat-label">Total Users</span>
            <span className="stat-value">{pagination.total}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Users with Wallets</span>
            <span className="stat-value">
              {users.filter(u => u.user.dogecoin_wallet).length}
            </span>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Status</th>
              <th>Balance</th>
              <th>Total Earned</th>
              <th>Articles Read</th>
              <th>Country</th>
              <th>Wallet</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((userRecord) => {
              const user = userRecord.user;
              const balance = userRecord.balance;

              return (
                <tr key={user.id}>
                  <td>
                    <div className="user-email">
                      {user.email}
                      {user.email === SUPER_ADMIN_EMAIL && (
                        <span className="badge badge-admin">SUPER ADMIN</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="status-badges">
                      {user.is_active ? (
                        <span className="badge badge-active">Active</span>
                      ) : (
                        <span className="badge badge-inactive">Inactive</span>
                      )}
                      {user.is_confirmed ? (
                        <span className="badge badge-confirmed">Confirmed</span>
                      ) : (
                        <span className="badge badge-unconfirmed">Unconfirmed</span>
                      )}
                    </div>
                  </td>
                  <td className="balance-cell">
                    {formatDoge(balance?.balance_doge)}
                  </td>
                  <td className="balance-cell">
                    {formatDoge(balance?.total_earned)}
                  </td>
                  <td>{balance?.articles_read_count || 0}</td>
                  <td>
                    {user.country_code ? (
                      <span title={user.country} style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                        {user.country_code}
                      </span>
                    ) : (
                      <span style={{ color: '#aaa' }}>—</span>
                    )}
                  </td>
                  <td>
                    {user.dogecoin_wallet ? (
                      <span className="wallet-address" title={user.dogecoin_wallet}>
                        {user.dogecoin_wallet.substring(0, 8)}...
                      </span>
                    ) : (
                      <span className="no-wallet">No wallet</span>
                    )}
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => handleEditUser(userRecord)}
                        title="Edit user"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-balance"
                        onClick={() => handleEditBalance(userRecord)}
                        title="Edit balance"
                      >
                        💰
                      </button>
                      {user.email !== SUPER_ADMIN_EMAIL && (
                        <button
                          className="btn-delete"
                          onClick={() => handleDeleteUser(userRecord)}
                          title="Delete user"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button
          disabled={pagination.skip === 0}
          onClick={() => setPagination(prev => ({
            ...prev,
            skip: Math.max(0, prev.skip - prev.limit)
          }))}
        >
          Previous
        </button>
        <span>
          Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
        </span>
        <button
          disabled={pagination.skip + pagination.limit >= pagination.total}
          onClick={() => setPagination(prev => ({
            ...prev,
            skip: prev.skip + prev.limit
          }))}
        >
          Next
        </button>
      </div>

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit User: {selectedUser.user.email}</h3>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Dogecoin Wallet</label>
              <input
                type="text"
                value={editForm.dogecoin_wallet}
                onChange={(e) => setEditForm({ ...editForm, dogecoin_wallet: e.target.value })}
                placeholder="Leave blank to clear"
              />
            </div>

            <div style={{ borderTop: '1px solid #eee', margin: '16px 0 12px', paddingTop: '12px' }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: '600', fontSize: '14px', color: '#333' }}>Rewards</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '12px' }}>Balance (DOGE)</label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={balanceForm.balance_doge}
                    onChange={(e) => setBalanceForm({ ...balanceForm, balance_doge: parseFloat(e.target.value) || 0 })}
                    style={{ padding: '6px', fontSize: '13px' }}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '12px' }}>Total Earned (DOGE)</label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={balanceForm.total_earned}
                    onChange={(e) => setBalanceForm({ ...balanceForm, total_earned: parseFloat(e.target.value) || 0 })}
                    style={{ padding: '6px', fontSize: '13px' }}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '12px' }}>Articles Read</label>
                  <input
                    type="number"
                    min="0"
                    value={balanceForm.articles_read_count}
                    onChange={(e) => setBalanceForm({ ...balanceForm, articles_read_count: parseInt(e.target.value) || 0 })}
                    style={{ padding: '6px', fontSize: '13px' }}
                  />
                </div>
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={editForm.is_active}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                />
                Active
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={editForm.is_confirmed}
                  onChange={(e) => setEditForm({ ...editForm, is_confirmed: e.target.checked })}
                />
                Email Confirmed
              </label>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={saveUserEdit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Balance Modal */}
      {showBalanceModal && (
        <div className="modal-overlay" onClick={() => setShowBalanceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Balance: {selectedUser.user.email}</h3>

            <div className="form-group">
              <label>Current Balance (DOGE)</label>
              <input
                type="number"
                step="0.01"
                value={balanceForm.balance_doge}
                onChange={(e) => setBalanceForm({ ...balanceForm, balance_doge: parseFloat(e.target.value) })}
              />
            </div>

            <div className="form-group">
              <label>Total Earned (DOGE)</label>
              <input
                type="number"
                step="0.01"
                value={balanceForm.total_earned}
                onChange={(e) => setBalanceForm({ ...balanceForm, total_earned: parseFloat(e.target.value) })}
              />
            </div>

            <div className="form-group">
              <label>Articles Read Count</label>
              <input
                type="number"
                value={balanceForm.articles_read_count}
                onChange={(e) => setBalanceForm({ ...balanceForm, articles_read_count: parseInt(e.target.value) })}
              />
            </div>

            <div className="reward-history-preview">
              <h4>Recent Reward History</h4>
              <div className="reward-list">
                {selectedUser.reward_history?.slice(0, 5).map((reward, idx) => (
                  <div key={idx} className="reward-item">
                    <span>{reward.reward_type}</span>
                    <span className="reward-amount">{formatDoge(reward.amount)}</span>
                    <span className="reward-date">{formatDate(reward.created_at)}</span>
                  </div>
                ))}
                {selectedUser.reward_history?.length === 0 && (
                  <div className="no-rewards">No reward history</div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowBalanceModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={saveBalanceEdit}>
                Update Balance
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;